import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import AppLayout from "./components/AppLayout";
import type { NavItem } from "./components/AppLayout";
import ClassificationIcon from "./components/ClassificationIcon";
import type { DeployEnv } from "./components/EnvSwitcher";
import FindingsIcon from "./components/FindingsIcon";
import InventoryIcon from "./components/InventoryIcon";
import ScorecardsIcon from "./components/ScorecardsIcon";
import Spinner from "./components/Spinner";
import { ToastProvider } from "./components/ToastProvider";
import { useAuth } from "./hooks/useAuth";
import Classification from "./pages/Classification";
import Findings from "./pages/Findings";
import Inventory from "./pages/Inventory";
import Scorecards from "./pages/Scorecards";
import SignIn from "./pages/SignIn";
import styles from "./App.module.css";

/** The views reachable from the rail. */
type NavId = "inventory" | "classification" | "scorecards" | "findings";

const NAV: NavItem[] = [
  { id: "inventory", label: "Inventory", icon: <InventoryIcon /> },
  { id: "classification", label: "Classification", icon: <ClassificationIcon /> },
  { id: "scorecards", label: "Scorecards", icon: <ScorecardsIcon /> },
  { id: "findings", label: "Findings", icon: <FindingsIcon /> },
];

const PAGES: Record<NavId, () => ReactElement> = {
  inventory: Inventory,
  classification: Classification,
  scorecards: Scorecards,
  findings: Findings,
};

/**
 * Narrow the rail's `string` id to a known view. AppLayout's onNavigate is
 * typed on `string`, so this guard keeps the cast out of the call site and
 * makes an unknown id a no-op rather than an invalid page lookup.
 */
function isNavId(id: string): id is NavId {
  return (
    id === "inventory" ||
    id === "classification" ||
    id === "scorecards" ||
    id === "findings"
  );
}

/**
 * Resolve the deployment environment shown in the rail indicator. Read from
 * the environment rather than hardcoded, per conventions/frontend-stack.md.
 * Falls back to "dev" when unset or unrecognized.
 */
function resolveEnv(): DeployEnv {
  const raw = import.meta.env.VITE_ENV;
  return raw === "prod" || raw === "stage" ? raw : "dev";
}


/**
 * Choose what to show for the signed-in user.
 *
 * KochID may release no `name` even with kochid_read granted, so fall back to
 * the email, which identifies a person. The email itself may be derived from
 * `sub`: KochID sets `sub` to the user's email address rather than an opaque
 * identifier (the UUID is in `p1.userId`), so that fallback yields a real
 * identity rather than a meaningless token. See utils/oidc.ts.
 */
function toDisplayIdentity(user: { name: string; email: string }): {
  name: string;
  email: string;
} {
  return {
    name: user.name || user.email || "Signed in",
    email: user.email,
  };
}

/**
 * The Inventory application. Renders the KCC shell only for an authenticated
 * user — protected content is never mounted while the access token is absent
 * (conventions/frontend-stack.md), so an unauthenticated visitor sees the
 * sign-in screen rather than a flash of the application.
 */
function App() {
  const { user, isAuthenticated, isLoading, error, isConfigured, login, logout } = useAuth();
  const [activeNav, setActiveNav] = useState<NavId>("inventory");
  const redirectStarted = useRef(false);

  // Send an unauthenticated visitor straight to KochID — no sign-in button.
  //
  // Four guards, each load-bearing:
  //  - isLoading: wait for the initial check and the callback exchange to
  //    finish, or we would redirect out of a login that is completing.
  //  - isAuthenticated: nothing to do once a token is held.
  //  - !isConfigured: with no VITE_KOCHID_* there is nowhere to redirect to;
  //    show the misconfiguration screen instead of looping.
  //  - error: THIS is the one that prevents an infinite loop. A failed or
  //    refused login lands back here with an error set; auto-redirecting on
  //    error would bounce app -> IdP -> error -> app -> IdP forever. On error
  //    we render a manual "Try again" instead.
  //
  // The ref makes it fire once: login() navigates the whole page away, and the
  // guard stops React StrictMode's double-invoke from starting two flows.
  useEffect(() => {
    if (isLoading || isAuthenticated || !isConfigured || error) {
      return;
    }
    if (redirectStarted.current) {
      return;
    }
    redirectStarted.current = true;
    login();
  }, [isLoading, isAuthenticated, isConfigured, error, login]);

  // The initial auth check, the callback exchange, and the outbound redirect
  // are all "work in progress" — show one spinner for all three rather than a
  // flash of any sign-in UI.
  if (isLoading || (!isAuthenticated && isConfigured && !error)) {
    return (
      <div className={styles.loading}>
        <Spinner size="lg" />
      </div>
    );
  }

  // Reached only when auto-redirect cannot proceed: KochID is not configured,
  // or a previous attempt failed. SignIn explains and offers a manual retry.
  if (!isAuthenticated || !user) {
    return <SignIn error={error} isConfigured={isConfigured} onSignIn={login} />;
  }

  const Page = PAGES[activeNav];

  return (
    <ToastProvider>
      <AppLayout
        env={resolveEnv()}
        user={toDisplayIdentity(user)}
        nav={NAV}
        activeNavId={activeNav}
        onNavigate={(id) => {
          if (isNavId(id)) {
            setActiveNav(id);
          }
        }}
        onSignOut={logout}
      >
        <Page />
      </AppLayout>
    </ToastProvider>
  );
}

export default App;
